import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import * as userActions from '../../store/actions/user';
import * as API from '../../store/sagas/api';
import ErrorMessage from '../../components/ErrorMessage';
import ErrorToast from '../../components/ErrorToast';
import { getOnboardingStepPath, isKycOrLaterOnboardingStep } from '../../utils/onboardingUtils';
import { FileTextIcon } from '../../assets/icons';
import { useOnboardingFooter } from '../../components/OnboardingFooterContext';

const inputClass =
  'mt-[3px] flex h-4 w-4 shrink-0 cursor-pointer rounded border border-[#DDE6EF] accent-[#0B1929] focus:ring-2 focus:ring-[#2E6BAD]/20 focus:ring-offset-2 disabled:cursor-default disabled:opacity-60';

const fallbackTermsContent = `
  <b>HansePay Terms & Conditions</b><br><br>
  By accepting, you agree to our payment services agreement, fee schedule, acceptable use policy, and GDPR data processing terms.
  You also confirm you have not been convicted of a serious financial crime.<br><br>
  <b>Payment Services Agreement:</b> HansePay provides regulated payment services in accordance with EU Payment Services Directive 2 (PSD2).
  All transactions are processed securely and subject to our fee schedule.<br><br>
  <b>Acceptable Use Policy:</b> You agree not to use HansePay for any illegal activities, money laundering, terrorist financing, or transactions
  involving sanctioned jurisdictions.<br><br>
  <b>Data Processing:</b> Your personal data is processed in accordance with EU Regulation 2016/679 (GDPR). You have the right to access, rectify,
  and erase your data.<br><br>
  <em>This is a placeholder - full terms to be inserted before go-live.</em>
`;

function TermsAndConditions() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const setFooterConfig = useOnboardingFooter();
  const [searchParams] = useSearchParams();
  const hasJustAcceptedRef = useRef(false);
  const hasAcceptedBlindPayFromRedirectRef = useRef(false);
  const {
    user,
    onboardingStatus,
    termsConditions,
    getTermsConditionsLoading,
    getTermsConditionsError,
    acceptingTermsConditions,
    acceptTermsConditionsError,
  } = useSelector((state) => state.user);
  const isAcceptLegalCompleted =
    Array.isArray(onboardingStatus?.steps) &&
    onboardingStatus.steps.some(
      (s) =>
        (typeof s === 'string' ? s === 'AcceptLegalAgreements' : s?.step === 'AcceptLegalAgreements') &&
        typeof s === 'object' &&
        s?.isCompleted === true,
    );
  const caplendAccepted = termsConditions?.accepted === true || isAcceptLegalCompleted;
  const blindPayAccepted = isAcceptLegalCompleted;

  const [accepted, setAccepted] = useState(caplendAccepted);
  const [blindPayLoading, setBlindPayLoading] = useState(false);
  const [blindPayError, setBlindPayError] = useState(null);
  const [blindPayAlreadyAccepted, setBlindPayAlreadyAccepted] = useState(false);
  const [toastError, setToastError] = useState(null);
  const blindPayDone = blindPayAccepted || blindPayAlreadyAccepted;
  const isKycOrLaterStep = isKycOrLaterOnboardingStep(onboardingStatus?.currentStep);

  const refreshBlindPayAcceptedStatus = useCallback(async () => {
    try {
      const response = await API.getBlindPayTosAcceptance();
      if (response?.accepted === true) {
        setBlindPayAlreadyAccepted(true);
        setBlindPayError(null);
        return true;
      }
      return false;
    } catch (_) {
      return false;
    }
  }, []);

  useEffect(() => {
    dispatch(userActions.getTermsConditions());
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    const checkBlindPayAccepted = async () => {
      try {
        const tosId = searchParams.get('tos_id');
        if (tosId && !hasAcceptedBlindPayFromRedirectRef.current) {
          hasAcceptedBlindPayFromRedirectRef.current = true;
          setBlindPayLoading(true);
          try {
            await API.acceptBlindPayTos(tosId);
            if (cancelled) return;
            try {
              sessionStorage.setItem('blindpay-tos-accepted', '1');
            } catch (_) {
              //
            }
            setBlindPayAlreadyAccepted(true);
            setBlindPayError(null);
            dispatch(userActions.getUser());
            dispatch(userActions.getOnboardingStatus());
          } catch (_) {
            //
          } finally {
            if (!cancelled) setBlindPayLoading(false);
            navigate('/accept-legal-agreements', { replace: true });
          }
          return;
        }

        try {
          const flag = sessionStorage.getItem('blindpay-tos-accepted');
          if (flag === '1') {
            sessionStorage.removeItem('blindpay-tos-accepted');
            setBlindPayAlreadyAccepted(true);
            setBlindPayError(null);
            return;
          }
        } catch (_) {
          //
        }
        const acceptedStatus = await refreshBlindPayAcceptedStatus();
        if (!cancelled && acceptedStatus) {
          setBlindPayAlreadyAccepted(true);
        }
      } catch (_) {
        //
      }
    };
    checkBlindPayAccepted();
    return () => {
      cancelled = true;
    };
  }, [refreshBlindPayAcceptedStatus, searchParams, dispatch, navigate]);

  useEffect(() => {
    if (caplendAccepted) setAccepted(true);
  }, [caplendAccepted]);

  useEffect(() => {
    if (
      hasJustAcceptedRef.current &&
      !acceptingTermsConditions &&
      !acceptTermsConditionsError &&
      onboardingStatus?.currentStep &&
      onboardingStatus.currentStep !== 'AcceptLegalAgreements'
    ) {
      const nextPath = getOnboardingStepPath(onboardingStatus.currentStep);
      if (nextPath) {
        hasJustAcceptedRef.current = false;
        navigate(nextPath, { replace: true });
      }
    }
  }, [acceptingTermsConditions, acceptTermsConditionsError, onboardingStatus?.currentStep, navigate]);

  const handleAcceptBlindPayTerms = async () => {
    if (isKycOrLaterStep) return;

    setBlindPayError(null);
    setBlindPayLoading(true);
    try {
      const idempotencyKey = uuidv4();
      const response = await API.generateBlindPayTosUrl(idempotencyKey);
      const url = response?.url;
      if (!url || typeof url !== 'string') {
        throw new Error('Could not get BlindPay terms URL');
      }
      window.location.href = url;
    } catch (err) {
      setBlindPayError(err?.message || err?.detail || 'Could not open BlindPay terms. Please try again.');
    } finally {
      setBlindPayLoading(false);
    }
  };

  const handleAccept = useCallback(() => {
    setToastError(null);

    if (isKycOrLaterStep) {
      const nextPath = onboardingStatus?.currentStep ? getOnboardingStepPath(onboardingStatus.currentStep) : null;
      if (nextPath) navigate(nextPath);
      return;
    }

    if (!user || !termsConditions?.version) {
      return;
    }

    if (!blindPayDone) {
      setToastError('BlindPay terms have not been accepted.');
      return;
    }

    if (caplendAccepted) {
      dispatch(userActions.getOnboardingStatus());
      return;
    }

    if (!accepted) {
      setToastError('Please accept the Terms and Conditions before continuing.');
      return;
    }

    hasJustAcceptedRef.current = true;
    dispatch(
      userActions.acceptTermsConditions({
        version: termsConditions.version,
        user,
      }),
    );
  }, [accepted, blindPayDone, caplendAccepted, dispatch, isKycOrLaterStep, navigate, onboardingStatus?.currentStep, termsConditions?.version, user]);

  useEffect(() => {
    setFooterConfig({
      continueLabel: acceptingTermsConditions ? 'Submitting...' : 'Accept & continue',
      continueDisabled:
        !isKycOrLaterStep &&
        ((!accepted && !caplendAccepted) || !blindPayDone || acceptingTermsConditions || getTermsConditionsLoading || !termsConditions?.version),
      onContinue: handleAccept,
    });
    return () => setFooterConfig({});
  }, [
    accepted,
    acceptingTermsConditions,
    blindPayDone,
    caplendAccepted,
    getTermsConditionsLoading,
    handleAccept,
    isKycOrLaterStep,
    setFooterConfig,
    termsConditions?.version,
  ]);

  const displayError =
    getTermsConditionsError?.message || getTermsConditionsError?.detail || acceptTermsConditionsError?.message || acceptTermsConditionsError?.detail;

  if (!user) {
    return null;
  }

  let blindPayButtonLabel = 'Accept BlindPay terms ->';
  if (blindPayDone) {
    blindPayButtonLabel = 'BlindPay terms accepted';
  } else if (blindPayLoading) {
    blindPayButtonLabel = 'Opening...';
  }

  return (
    <div className='flex justify-center bg-[#EBF4FB] px-4 py-9 font-sans text-[#0B1929] sm:px-6'>
      <ErrorToast error={toastError} onDismiss={() => setToastError(null)} />
      <div className='w-full max-w-[540px] rounded-[20px] bg-white px-6 py-8 shadow-[0_4px_32px_rgba(11,25,41,0.08)] sm:px-12 sm:py-11'>
        <div>
          <div className='mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#EBF4FB] text-[#1E4E80]'>
            <FileTextIcon className='h-6 w-6' />
          </div>
          <h1 className='mb-2.5 font-serif text-2xl font-normal leading-[1.25] text-[#0B1929]'>
            Almost there.
          </h1>
          <p className='mt-2 text-sm leading-6 text-[#4E6F8A]'>Please review and accept our terms.</p>
        </div>

        <div className='mt-8 space-y-4'>
          <div className='max-h-[260px] overflow-y-auto rounded-[10px] border border-[#E2EBF3] bg-[#F8FBFE] p-5 text-left text-[13px] leading-6 text-[#3D5A73]'>
            {getTermsConditionsLoading && <p className='text-sm text-[#7A9AB0]'>Loading terms...</p>}
            {getTermsConditionsError && <ErrorMessage error={displayError} />}
            {!getTermsConditionsLoading && (
              <div
                className='prose prose-sm max-w-none text-[13px] leading-6 text-[#3D5A73] prose-headings:text-[#0B1929] prose-p:text-[#3D5A73] prose-strong:text-[#0B1929]'
                /* eslint-disable-next-line react/no-danger */
                dangerouslySetInnerHTML={{ __html: termsConditions?.content || fallbackTermsContent }}
              />
            )}
          </div>

          <div className='rounded-[10px] border border-[#E2EBF3] bg-[#FAFCFF] p-4'>
            <div className='mb-3 flex items-center justify-between gap-3'>
              <h4 className='text-[13px] font-semibold text-[#0B1929]'>BlindPay Payment Services</h4>
              <span
                className={
                  blindPayDone
                    ? 'shrink-0 rounded-full bg-[#E8F7EF] px-2.5 py-1 text-[11.5px] font-semibold leading-none text-[#1A7A4A]'
                    : 'shrink-0 rounded-full bg-[#F4F8FC] px-2.5 py-1 text-[11.5px] font-semibold leading-none text-[#7A9AB0]'
                }
              >
                {blindPayDone ? 'Accepted ✓' : 'Not accepted'}
              </span>
            </div>
            <p className='mb-3 text-[13px] leading-5 text-[#3D5A73]'>
              To use payment services you must also accept BlindPay&apos;s terms.
            </p>
            {blindPayError && <ErrorMessage error={blindPayError} />}
            {!blindPayDone && (
              <button
                type='button'
                onClick={handleAcceptBlindPayTerms}
              disabled={blindPayLoading || isKycOrLaterStep}
                className='inline-flex h-9 w-auto items-center justify-center rounded-full border border-[#0B1929]/15 bg-white px-4 text-[13px] font-semibold text-[#3D5A73] transition hover:border-[#8DBDE6] hover:bg-[#EBF4FB] disabled:pointer-events-none disabled:opacity-50'
              >
                {blindPayButtonLabel}
              </button>
            )}
          </div>

          <div
            className='flex cursor-pointer items-start gap-2.5'
            onClick={(e) => {
              if (e.target.id === 'terms' || e.target.htmlFor === 'terms') return;
              if (!getTermsConditionsLoading && !caplendAccepted) {
                setAccepted((prev) => !prev);
              }
            }}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !getTermsConditionsLoading && !caplendAccepted) {
                e.preventDefault();
                setAccepted((prev) => !prev);
              }
            }}
            role='button'
            tabIndex={0}
            aria-pressed={caplendAccepted || accepted}
          >
            <input
              id='terms'
              type='checkbox'
              checked={caplendAccepted || accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className={inputClass}
              disabled={getTermsConditionsLoading || caplendAccepted || isKycOrLaterStep}
            />
            <div className='flex flex-1 flex-col'>
              <label htmlFor='terms' className='cursor-pointer text-[13px] leading-relaxed text-[#3D5A73]'>
              By accepting this, you agree to our <span className='font-medium text-[#2E6BAD]'>Terms of Service</span>, and{' '}
              that you have read and understood our <span className='font-medium text-[#2E6BAD]'>Privacy Policy</span> as well as <span className='font-medium text-[#2E6BAD]'>Cookie Policy</span> .
              </label>
            </div>
          </div>

          {getTermsConditionsError && <ErrorMessage error={displayError} />}
          {acceptTermsConditionsError && <ErrorMessage error={displayError} />}
          <p className='text-[12px] leading-5 text-[#7A9AB0]'>Your data is protected under GDPR.</p>
        </div>
      </div>
    </div>
  );
}

export default TermsAndConditions;
